import { t } from '@/features/i18n/utils';
import { WorkspaceBreadcrumbs } from '@/features/workspaces/components/workspace-breadcrumbs';
import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';
import z from 'zod';
import { BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderChatButton, HeaderBackButton } from '@/ui/components/header';
import { contractReviewQuery } from '@/features/contract-review/queries';
import {
  contractReviewClusterIdAtom,
  contractReviewClusterPanelOpenAtom,
} from '@/features/contract-review/store';
import { humanizeDomainName } from '@/features/contract-review/utils';
import { ContractReviewDomainPage } from '@/features/contract-review/components/domain-page/contract-review-domain-page';
import { commentPanelOpenAtom } from '@/features/comments/store';
import { useWorkspace } from '@/features/workspaces/helpers';
import { workspaceQuery } from '@/features/workspaces/queries';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/_layout/(main)/contract-review/$domain',
)({
  staticData: { chatSurface: 'legal_deviation' },
  component: RouteComponent,
  validateSearch: z.object({
    clusterId: z.string().optional(),
    commentId: z.string().optional(),
  }),
  loaderDeps: ({ search }) => ({ clusterId: search.clusterId, commentId: search.commentId }),
  loader: async ({ context, params, deps, preload }) => {
    if (!preload && deps.clusterId) {
      const store = getDefaultStore();
      store.set(contractReviewClusterIdAtom, deps.clusterId);
      store.set(deps.commentId ? commentPanelOpenAtom : contractReviewClusterPanelOpenAtom, true);
    }
    const workspace = await context.queryClient.query({
      ...workspaceQuery(params.workspaceId),
      staleTime: 'static',
    });
    return context.queryClient.query({
      ...contractReviewQuery(workspace.type, params.workspaceId),
      staleTime: 'static',
    });
  },
});

function RouteComponent() {
  const { projectId, workspaceId, domain } = Route.useParams();
  const workspace = useWorkspace();
  const contractReview = useSuspenseQuery(contractReviewQuery(workspace.type, workspaceId));

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId/contract-review"
          params={{ projectId, workspaceId }}
        />
        <WorkspaceBreadcrumbs>
          <BreadcrumbLink
            to="/project/$projectId/workspaces/$workspaceId/contract-review"
            params={{ projectId, workspaceId }}
          >
            {t({ en: 'Contract review', nb: 'Kontraktsgjennomgang' })}
          </BreadcrumbLink>
          <BreadcrumbPage>
            {contractReview.data.domains.find((item) => item.domain === domain)?.name ??
              humanizeDomainName(domain)}
          </BreadcrumbPage>
        </WorkspaceBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <ContractReviewDomainPage />
    </>
  );
}
